// Centralised multilingual content for CHINTAMAT
import { appStrings } from "./appStrings";
import { deepMergeLocale } from "./merge";
import { rawBn, appStringsBn } from "./locales/bn";
import { rawMr, appStringsMr } from "./locales/mr";
import { rawKn, appStringsKn } from "./locales/kn";
import { rawMl, appStringsMl } from "./locales/ml";
import { rawPa, appStringsPa } from "./locales/pa";

const _raw = {
  en: {
    nav: {
      home: "Home",
      database: "Scammer Database",
      recovery: "Recovery Process",
      risk: "Risk Checker",
      redFlags: "Red Flags",
      education: "Education",
      stories: "Real Stories",
      about: "About",
      contact: "Contact",
      legalHelp: "Legal Help",
      reportCta: "Report Loss",
      reportTradingLoss: "Report trading loss",
      stockMarket: "Stock Market",
      mentors: "Mentors",
      dashboard: "Dashboard",
      signIn: "Sign in",
      signOut: "Sign out",
    },
    common: {
      reportScam: "Report Loss",
      learnRecovery: "Learn Recovery Process",
      confidential: "100% Confidential",
      readMore: "Read more",
      backHome: "Back to home",
      search: "Search",
      verified: "Verified",
      anonymous: "Anonymous",
      submit: "Submit",
      cancel: "Cancel",
      requiredField: "Required",
      check: "Check now",
      loading: "Loading…",
      error: "Something went wrong. Try again.",
    },
    hero: {
      eyebrow: "India's Evidence-Based Financial Complaint Guidance Platform",
      title1: "Fight Back Against",
      title2: "Financial Loss.",
      subtitle:
        "From trading fraud to banking, cyber, insurance, airline, consumer, housing-finance and pension disputes — we help you find the right authority, prepare the right evidence, and take the right next step through official Indian complaint systems.",
      reportBtn: "Report a Financial Loss",
      recoveryBtn: "Learn the Complaint Process",
      taglines: [
        "Find the right authority.",
        "Prepare the right evidence.",
        "Take the right next step.",
      ],
    },
    trust: {
      title: "Built on Trust, Backed by Evidence",
      items: [
        { title: "100% Confidential", desc: "Reports are anonymous by default. We never expose your identity." },
        { title: "Evidence-First", desc: "Every complaint is built on verifiable proof, not hearsay." },
        { title: "Honest Guidance", desc: "We explain official complaint routes clearly — without false promises or pressure tactics." },
        { title: "Official Channels Only", desc: "We guide you to government regulators — RBI, SEBI, IRDAI, consumer forums and more." },
      ],
    },
    scams: {
      title: "Where Financial Losses Happen",
      subtitle:
        "Losses can come from banking, digital payments, insurance, shopping, loans, investments, or impersonation scams. Recognise the patterns before money moves.",
      categories: [
        { key: "upi_fraud", title: "UPI & Digital Payment Fraud", desc: "Fake payment links, QR swaps, wrong-account transfers, and spoofed UPI collect requests.", icon: "Send" },
        { key: "phishing", title: "Phishing & Impersonation", desc: "Callers posing as bank, RBI, police, or tax officials to steal OTPs and credentials.", icon: "BadgeCheck" },
        { key: "unauth_debit", title: "Unauthorised Bank Debits", desc: "Sudden card/UPI debits, mandate abuse, or account access without your consent.", icon: "KeyRound" },
        { key: "insurance", title: "Insurance Mis-selling", desc: "Policies sold as investments, hidden charges, or claims rejected on technical grounds.", icon: "TrendingUp" },
        { key: "consumer", title: "Online Shopping & Refund Fraud", desc: "Paid but not delivered, fake marketplaces, refund denials, and defective goods disputes.", icon: "MessageSquare" },
        { key: "loan_app", title: "Predatory Loan Apps", desc: "Harassment, hidden fees, data blackmail, and unauthorised debits after small loans.", icon: "Image" },
        { key: "recovery", title: "Fake Recovery Agents", desc: "Scammers who contact victims again and demand fees to 'recover' money already lost.", icon: "RefreshCcw" },
        { key: "investment", title: "Unregistered Investment Schemes", desc: "Chit funds, MLM, crypto platforms, and guaranteed-return schemes with no valid licence.", icon: "BookOpen" },
      ],
    },
    recovery: {
      title: "How Complaint Resolution Works",
      subtitle: "A practical framework for any financial loss in India — document, file at the right authority, follow up, and escalate when needed.",
      steps: [
        { title: "Document Everything", desc: "Save receipts, bank/UPI references, invoices, contracts, emails, and chat exports with dates and amounts." },
        { title: "Secure Your Accounts", desc: "Change passwords, revoke app access, block cards, and notify your bank or wallet provider immediately." },
        { title: "Pick the Right Authority", desc: "Banking → RBI CMS; cyber fraud → cybercrime.gov.in; insurance → IRDAI; airlines → AirSewa; products → consumer helpline." },
        { title: "File at the Official Portal", desc: "Submit your complaint on the regulator's website. Attach evidence and write a factual timeline — not emotional language." },
        { title: "Keep Every Reference Number", desc: "Complaint ID, FIR number, helpline ticket, and bank dispute reference — you will need these for follow-up." },
        { title: "Follow Up & Escalate", desc: "If no response, use ombudsman routes, consumer commissions, or SMART ODR where applicable." },
        { title: "Avoid Secondary Scams", desc: "Do not pay strangers who promise guaranteed recovery. Verify any paid consultation separately before committing." },
      ],
      disclaimer: "CHINTAMAT is not a law firm. We do not guarantee outcomes. We guide you through official Indian complaint systems.",
    },
    official: {
      title: "Official Help Links",
      subtitle: "Direct links to India's regulatory complaint portals — pick the authority that matches your loss. Each opens in a new tab.",
      items: [
        { title: "SEBI SCORES", desc: "Trading & investment complaints against registered intermediaries.", url: "https://scores.sebi.gov.in" },
        { title: "RBI CMS", desc: "Banking, UPI, card, loan & NBFC complaints.", url: "https://cms.rbi.org.in" },
        { title: "Cyber Crime Portal", desc: "Online fraud & scam payments. Helpline 1930.", url: "https://cybercrime.gov.in" },
        { title: "IRDAI Bima Bharosa", desc: "Insurance claim & policy disputes.", url: "https://bimabharosa.irdai.gov.in" },
        { title: "AirSewa", desc: "Airline refund, delay, baggage & cancellation issues (DGCA).", url: "https://airsewa.gov.in" },
        { title: "National Consumer Helpline", desc: "Product & service disputes; consumer commission.", url: "https://consumerhelpline.gov.in" },
        { title: "NHB", desc: "Housing-finance company complaints.", url: "https://nhb.org.in" },
        { title: "PFRDA", desc: "NPS & pension-related grievances.", url: "https://www.pfrda.org.in" },
      ],
    },
    report: {
      title: "Report a Scam Anonymously",
      subtitle: "Your identity is kept strictly confidential. All submissions are reviewed before any public listing.",
      scamType: "Type of scam",
      amountLost: "Amount lost (INR)",
      platform: "Platform used",
      telegram: "Telegram username (if any)",
      upi: "UPI ID (if any)",
      phone: "Phone number (if any)",
      company: "Company / Advisor name",
      website: "Website URL",
      description: "Describe what happened",
      contact: "Email (optional, for follow-up)",
      uploadEvidence: "Upload Evidence",
      uploadHelp: "Screenshots, PDFs, payment proofs, chat exports. Max 10MB per file.",
      submit: "Submit Report Securely",
      successTitle: "Report Submitted",
      successDesc: "Thank you. Our team will review your evidence within 72 hours. Your identity remains confidential.",
      disclaimer:
        "All accusations require supporting evidence. Defamatory or false submissions may be rejected. Submissions are reviewed before publication.",
      placeholders: {
        description: "Include dates, amounts, what was promised, and what actually happened. The more specific, the better.",
      },
      types: [
        "Telegram Premium Group",
        "Fake SEBI Advisor",
        "Research Analyst / Research Advisory",
        "Account Handling Fraud",
        "Guaranteed Profit Scheme",
        "WhatsApp Signal Pump",
        "Fake Profit Screenshots",
        "Forex / Crypto Recovery Scam",
        "Paid Course Scam",
        "Other",
      ],
    },
    database: {
      title: "Scammer Database",
      subtitle: "Searchable, evidence-backed database of reported entities. Search by Telegram, UPI, phone, or company.",
      watchlistTitle: "Top Reported Scammers",
      watchlistSubtitle: "Evidence-backed entries from our verified database. Every handle here is corroborated by multiple victim reports.",
      watchlistLink: "View full search",
      searchPlaceholder: "Search Telegram, UPI, phone, company…",
      noResults: "No matches in our verified database.",
      victims: "victims",
      lossReported: "loss reported",
      statusLabels: {
        high_risk: "HIGH RISK",
        reported_cybercrime: "REPORTED TO CYBER CRIME",
        sebi_warning: "SEBI WARNING",
        multiple_complaints: "MULTIPLE COMPLAINTS",
        under_investigation: "UNDER INVESTIGATION",
      },
      filterAll: "All statuses",
    },
    risk: {
      title: "Scam Risk Checker",
      subtitle: "Enter a Telegram username, UPI ID, phone, or website to check against our reported scammer database.",
      telegram: "Telegram username",
      upi: "UPI ID",
      phone: "Phone number",
      website: "Website URL",
      check: "Check Risk",
      verdictLabels: {
        DANGER: "DANGER — CONFIRMED SCAMMER",
        "HIGH RISK": "HIGH RISK — MULTIPLE COMPLAINTS",
        CAUTION: "CAUTION — POSSIBLE MATCH",
        "NO MATCH": "NO MATCH IN OUR DATABASE",
      },
      noMatchHelp:
        "No match yet doesn't mean it's safe. Use the Red Flag Detector to evaluate the operator's behaviour.",
    },
    redFlag: {
      title: "Red Flag Detector",
      subtitle: "Tick every behaviour you've observed. We'll calculate a scam probability score.",
      checklist: [
        { key: "guaranteed_profits", label: "Promises guaranteed profits or fixed % returns" },
        { key: "fake_sebi_certificate", label: "Shows a SEBI certificate but you can't verify on sebi.gov.in" },
        { key: "asks_remote_access", label: "Asks for remote access or your trading login" },
        { key: "asks_upi_to_personal", label: "Asks you to pay to a personal UPI / individual account" },
        { key: "pressure_tactics", label: "Pressure tactics — 'limited slots', 'today only'" },
        { key: "fake_screenshots", label: "Shows profit screenshots without broker statement" },
        { key: "no_written_agreement", label: "No written agreement, no GST invoice" },
        { key: "unverified_advisor", label: "Influencer / Telegram admin with no verifiable credentials" },
      ],
      score: "Scam probability",
      flagged: "Red flags detected",
    },
    education: {
      eyebrow: "Knowledge Base",
      title: "Financial Loss Education",
      subtitle: "Understand how losses happen, how to avoid them, how to choose legitimate services, and how to fight back through official Indian complaint channels.",
      understandingTitle: "How financial losses happen",
      understandingDesc: "Most losses follow predictable patterns — urgency, impersonation, and payments to unverified accounts. Knowing the pattern helps you stop before you pay.",
      understandingTopics: [
        {
          title: "Digital payment & banking fraud",
          desc: "Fake customer-care numbers, malicious links, SIM swaps, and OTP sharing are the most common routes to UPI and account theft.",
          points: ["Never share OTPs or PINs — even if the caller claims to be from your bank.", "Verify helpline numbers on the bank's official website, not Google ads.", "Treat unexpected refund or KYC messages as suspicious until verified."],
        },
        {
          title: "Insurance & financial product mis-selling",
          desc: "Products sold as 'guaranteed savings' may be unsuitable policies with high charges and unclear exit terms.",
          points: ["Ask for the policy document and benefit illustration in writing before paying.", "Check IRDAI registration and compare with alternatives.", "Mis-selling complaints go to IRDAI Bima Bharosa or the insurer's grievance cell first."],
        },
        {
          title: "Consumer & marketplace disputes",
          desc: "Online orders, subscriptions, travel bookings, and home services can fail to deliver or deny rightful refunds.",
          points: ["Keep order IDs, payment proofs, and all communication.", "Use National Consumer Helpline (1915) and e-daakhil portal for formal complaints.", "Chargebacks and platform grievance cells are first steps for card/UPI payments."],
        },
        {
          title: "Investment & scheme fraud",
          desc: "Unregistered schemes, chit funds, MLM, and fake trading platforms promise returns they cannot legally deliver.",
          points: ["Verify registration on SEBI, RBI, or MCA portals before investing.", "Guaranteed high returns with low risk is a universal red flag.", "If already invested, document transfers and marketing claims immediately."],
        },
      ],
      preventionTitle: "How to avoid losing money",
      preventionItems: [
        "Pause before any urgent payment request — scammers create artificial deadlines.",
        "Pay only to verified company accounts; avoid personal UPI IDs for business transactions.",
        "Read agreements, refund policies, and fee schedules before signing or transferring.",
        "Use two-factor authentication and transaction alerts on all financial apps.",
        "Search the entity name plus 'complaint' or 'fraud' before committing large amounts.",
        "Never grant remote access to your phone or laptop for 'KYC' or 'refund processing'.",
      ],
      chooseServiceTitle: "How to choose financial services safely",
      chooseServiceIntro: "Whether you need banking help, insurance, investment advice, or dispute support — legitimacy comes from verifiable credentials, written terms, and regulated channels.",
      chooseServiceChecks: [
        { title: "Verify registration", desc: "Cross-check licences on RBI, SEBI, IRDAI, or MCA websites — not on the provider's PDF alone." },
        { title: "Demand written terms", desc: "Fees, refunds, dispute process, and scope of service must be documented before you pay." },
        { title: "Reject pressure tactics", desc: "'Limited seats', 'today only', or threats of legal action are common scam signals." },
        { title: "Evaluate paid help carefully", desc: "Lawyers, consultants, and recovery services should explain fees upfront. Compare options; avoid advance-fee recovery agents." },
        { title: "Use official portals first", desc: "Regulator complaint systems exist for most loss types — they are your primary route, not optional extras." },
      ],
      fightTitle: "How to fight a financial loss",
      fightIntro: "Act quickly, stay factual, and chain complaints across the right authorities. Emotional messages without evidence slow resolution.",
      fightSteps: [
        { step: "01", title: "Stop further loss", desc: "Block cards, revoke mandates, and alert your bank or wallet provider within the first hour if possible." },
        { step: "02", title: "Build an evidence file", desc: "One folder with chronology, amounts, reference numbers, screenshots, and copies of all communications." },
        { step: "03", title: "File at the correct portal", desc: "Cyber fraud, banking, insurance, airline, consumer, and investment each have dedicated official channels." },
        { step: "04", title: "Track and escalate", desc: "Note every complaint ID. If the first forum stalls, use ombudsman, consumer commission, or SMART ODR as applicable." },
        { step: "05", title: "Get structured guidance", desc: "CHINTAMAT can help you map your loss type, prepare documentation, and understand next steps through official systems." },
      ],
      filingGuidesTitle: "Official filing guides by loss type",
      filingGuidesDesc: "Step-by-step portal instructions and CHINTAMAT help forms — use these when you are ready to file, after reading the sections above.",
      articlesTitle: "Reference guides",
      articlesDesc: "Curated explainers on complaint filing and scam awareness — not victim stories.",
      excludeCategories: ["Real Stories"],
      readMinutes: "min read",
      filterAll: "All",
      emptyCategory: "No articles in this category yet.",
    },
    blog: {
      eyebrow: "Investigations · Stories · Playbooks",
      title: "Blog — Financial Loss Cases & Filing Guides",
      subtitle:
        "Real cases and step-by-step complaint guides for trading fraud, banking/UPI disputes, cyber scams, insurance, airlines, consumer issues, housing finance, pension, and more — drawn from public forums and official sources.",
      allStories: "All Stories",
      featured: "Featured",
      readFull: "Read in full",
      readArticle: "Read full article",
      collapse: "Collapse",
      emptyCategory: "No articles in this category yet.",
      newBadge: "New",
      ctaTitle: "Lost money and need guidance?",
      ctaBody:
        "Choose your loss type, read the official filing steps, and submit to CHINTAMAT for team review — confidential and evidence-based.",
      ctaButton: "Report Loss",
    },
    stories: {
      title: "Real Victim Stories",
      subtitle: "Anonymous accounts from people who lost money to banking fraud, insurance disputes, shopping scams, loan apps, and other financial harm — not just trading.",
      lossLabel: "Loss",
      lessonLabel: "What I learned",
    },
    about: {
      title: "Why CHINTAMAT Exists",
      subtitle:
        "Indians lose thousands of crores every year to banking fraud, cyber scams, insurance mis-selling, consumer disputes, and unregistered schemes. Victims often do not know which authority to approach or how to document their case.",
      mission:
        "CHINTAMAT is an independent, evidence-based platform that helps people fight financial losses — by mapping the right complaint channel, building verifiable case files, and exposing repeat offenders through a public database.",
      pillars: [
        { title: "Confidentiality", desc: "All reports are anonymous unless the victim explicitly chooses to identify themselves." },
        { title: "Evidence-Backed", desc: "We publish profiles and guidance only when claims are supported by verifiable documentation." },
        { title: "Official Guidance", desc: "We point victims to RBI, SEBI, IRDAI, cybercrime, consumer, and other regulator portals — not unverified recovery agents." },
      ],
      notDo: {
        title: "What CHINTAMAT does NOT do",
        items: [
          "Provide investment tips, trading signals, or guaranteed-return advice.",
          "Sell unverified courses or act as a broker or agent.",
          "Access or manage anyone's bank, Demat, or trading account.",
          "Guarantee fund recovery or pose as a law firm or police agency.",
          "Accept paid promotion from the entities we investigate.",
        ],
      },
      disclaimer: "CHINTAMAT is not a law firm and not a financial advisor. We guide victims toward official Indian complaint systems.",
    },
    legalHelp: {
      eyebrow: "Guidance · Not legal representation",
      title: "Legal Help",
      subtitle:
        "Describe your financial loss or consumer dispute. We help document your case and point you to official Indian complaint channels — not unverified recovery agents.",
      disclaimer:
        "CHINTAMAT is not a law firm. We do not guarantee outcomes.",
      name: "Your name",
      email: "Email",
      phone: "Phone",
      optional: "optional",
      lossType: "Type of loss",
      amountLost: "Amount lost (INR)",
      callFromAgent: "Someone claiming to be a lawyer, police officer, or recovery agent contacted me about this case",
      description: "Brief description",
      descriptionPlaceholder: "What happened, dates, and any reference numbers you already have…",
      submit: "Submit legal help request",
      required: "Email and type of loss are required",
      success: "Request received. Our team will review it shortly.",
      dashboardHint: "Track submissions on your",
      dashboardLink: "dashboard",
      viewDashboard: "View my legal help requests →",
      footerNote: "For trading scams, also see",
    },
    contact: {
      title: "Get in Touch",
      subtitle: "Email is the preferred and most secure channel. No phone support is offered, by design.",
      name: "Your name (or pseudonym)",
      email: "Email",
      subject: "Subject",
      message: "Message",
      send: "Send Message",
      typeGeneral: "General Enquiry",
      typeMedia: "Media / Press",
      typeAnon: "Anonymous Tip",
      success: "Message sent. We'll respond within 3-5 working days.",
    },
    footer: {
      tagline: "Evidence over hearsay. Protection over silence.",
      sections: {
        platform: "Platform",
        legal: "Legal",
        connect: "Connect",
      },
      links: {
        privacy: "Privacy Policy",
        terms: "Terms of Use",
        disclaimer: "Disclaimer",
        antiHarassment: "Anti-Harassment Policy",
        dataDeletion: "Data Deletion Request",
      },
      disclaimer:
        "CHINTAMAT does not provide financial advice. We do not guarantee fund recovery. All accusations require supporting evidence.",
      rights: "© 2026 CHINTAMAT. A public-interest anti-scam mission.",
    },
    stats: {
      reports: "Reports Verified",
      scammers: "Scammers Tracked",
      cases: "Cases Resolved",
      loss: "Loss Reported",
    },
    reportExtra: {
      privacyIdentity:
        "Your identity is not stored unless you provide a contact email.",
      privacyReview:
        "Every report passes human review before any public listing.",
      selectPlaceholder: "Select…",
      submitAnother: "Submit another",
      selectScamType: "Select scam type",
      descMin: "Description must be at least 20 characters",
      uploadTooLarge: "file too large (10MB max)",
      uploadFailed: "upload failed",
      uploadSuccess: "Evidence uploaded securely",
    },
  },

  hi: {
    nav: {
      home: "मुख्य पृष्ठ",
      database: "स्कैमर डेटाबेस",
      recovery: "रिकवरी प्रक्रिया",
      risk: "रिस्क चेकर",
      redFlags: "रेड फ्लैग",
      education: "जागरूकता",
      stories: "सच्ची कहानियाँ",
      about: "हमारे बारे में",
      contact: "संपर्क",
      legalHelp: "कानूनी सहायता",
      reportCta: "स्कैम रिपोर्ट करें",
      mentors: "मेंटर",
      dashboard: "डैशबोर्ड",
      signIn: "साइन इन",
      signOut: "साइन आउट",
    },
    common: {
      reportScam: "स्कैम रिपोर्ट करें",
      learnRecovery: "रिकवरी प्रक्रिया सीखें",
      confidential: "100% गोपनीय",
      readMore: "और पढ़ें",
      backHome: "मुख्य पृष्ठ पर लौटें",
      search: "खोजें",
      verified: "सत्यापित",
      anonymous: "गुमनाम",
      submit: "जमा करें",
      cancel: "रद्द करें",
      requiredField: "आवश्यक",
      check: "अभी जाँचें",
      loading: "लोड हो रहा है…",
      error: "कुछ गलत हुआ। दोबारा कोशिश करें।",
    },
    hero: {
      eyebrow: "भारत का साक्ष्य-आधारित वित्तीय शिकायत मार्गदर्शन प्लेटफ़ॉर्म",
      title1: "वित्तीय नुकसान",
      title2: "के खिलाफ लड़िए।",
      subtitle:
        "ट्रेडिंग धोखाधड़ी से लेकर बैंकिंग, साइबर, बीमा, एयरलाइन, उपभोक्ता, हाउसिंग फाइनेंस और पेंशन विवाद तक — हम आपको सही अथॉरिटी खोजने, सही सबूत तैयार करने और आधिकारिक भारतीय शिकायत प्रणालियों से सही अगला कदम उठाने में मदद करते हैं।",
      reportBtn: "वित्तीय नुकसान रिपोर्ट करें",
      recoveryBtn: "शिकायत प्रक्रिया सीखें",
      taglines: [
        "सही अथॉरिटी खोजें।",
        "सही सबूत तैयार करें।",
        "सही अगला कदम उठाएँ।",
      ],
    },
    trust: {
      title: "विश्वास, सबूत के साथ",
      items: [
        { title: "100% गोपनीय", desc: "रिपोर्ट गुमनाम हैं। हम आपकी पहचान कभी सार्वजनिक नहीं करते।" },
        { title: "सबूत-पहले", desc: "हर शिकायत सत्यापित सबूतों पर आधारित होती है, सुनी-सुनाई बातों पर नहीं।" },
        { title: "ईमानदार मार्गदर्शन", desc: "हम आधिकारिक शिकायत मार्ग स्पष्ट बताते हैं — बिना झूठे वादे या दबाव के।" },
        { title: "केवल आधिकारिक चैनल", desc: "हम आपको RBI, SEBI, IRDAI, उपभोक्ता फोरम और अन्य सरकारी नियामकों तक पहुँचाते हैं।" },
      ],
    },
    scams: {
      title: "वित्तीय नुकसान कहाँ होते हैं",
      subtitle: "नुकसान बैंकिंग, डिजिटल भुगतान, बीमा, खरीदारी, लोन, निवेश या पहचान की धोखाधड़ी से हो सकता है। पैसा जाने से पहले पैटर्न पहचानें।",
      categories: [
        { key: "upi_fraud", title: "UPI और डिजिटल भुगतान धोखाधड़ी", desc: "नकली लिंक, QR बदलाव, गलत खाते में ट्रांसफर, और नकली collect अनुरोध।", icon: "Send" },
        { key: "phishing", title: "फ़िशिंग और पहचान की धोखाधड़ी", desc: "बैंक, RBI, पुलिस या कर अधिकारी बनकर OTP और क्रेडेंशियल चुराना।", icon: "BadgeCheck" },
        { key: "unauth_debit", title: "अनधिकृत बैंक डेबिट", desc: "अचानक कार्ड/UPI डेबिट, mandate दुरुपयोग, या बिना सहमति खाता एक्सेस।", icon: "KeyRound" },
        { key: "insurance", title: "बीमा mis-selling", desc: "निवेश के रूप में बेची गई पॉलिसी, छिपे शुल्क, या तकनीकी आधार पर अस्वीकृत क्लेम।", icon: "TrendingUp" },
        { key: "consumer", title: "ऑनलाइन खरीदारी और रिफंड धोखाधड़ी", desc: "भुगतान के बाद डिलीवरी नहीं, नकली मार्केटप्लेस, रिफंड से इनकार।", icon: "MessageSquare" },
        { key: "loan_app", title: "शोषक लोन ऐप", desc: "उत्पीड़न, छिपे शुल्क, डेटा ब्लैकमेल, और छोटे लोन के बाद अनधिकृत डेबिट।", icon: "Image" },
        { key: "recovery", title: "नकली रिकवरी एजेंट", desc: "पीड़ितों से संपर्क कर 'खोई रकम वापस' दिलाने के लिए शुल्क मांगने वाले।", icon: "RefreshCcw" },
        { key: "investment", title: "अपंजीकृत निवेश योजनाएँ", desc: "चिट फंड, MLM, क्रिप्टो प्लेटफ़ॉर्म, और गारंटीड रिटर्न बिना वैध लाइसेंस के।", icon: "BookOpen" },
      ],
    },
    recovery: {
      title: "शिकायत समाधान कैसे काम करता है",
      subtitle: "भारत में किसी भी वित्तीय नुकसान के लिए व्यावहारिक ढाँचा — दस्तावेज़, सही अथॉरिटी पर दायर, फॉलो-अप और एस्केलेशन।",
      steps: [
        { title: "सब कुछ दर्ज करें", desc: "रसीद, बैंक/UPI रेफ़, चालान, अनुबंध, ईमेल और चैट निर्यात — तारीख और रकम के साथ।" },
        { title: "खाते सुरक्षित करें", desc: "पासवर्ड बदलें, ऐप एक्सेस रद्द करें, कार्ड ब्लॉक करें, तुरंत बैंक को सूचित करें।" },
        { title: "सही अथॉरिटी चुनें", desc: "बैंकिंग → RBI CMS; साइबर → cybercrime.gov.in; बीमा → IRDAI; एयरलाइन → AirSewa; उत्पाद → उपभोक्ता हेल्पलाइन।" },
        { title: "आधिकारिक पोर्टल पर दायर करें", desc: "नियामक की वेबसाइट पर शिकायत दर्ज करें। सबूत संलग्न करें, तथ्यात्मक समयरेखा लिखें।" },
        { title: "हर रेफ़रेंस नंबर रखें", desc: "शिकायत ID, FIR नंबर, हेल्पलाइन टिकट, बैंक विवाद संदर्भ — फॉलो-अप के लिए ज़रूरी।" },
        { title: "फॉलो-अप और एस्केलेट", desc: "कोई जवाब नहीं → ombudsman, उपभोक्ता आयोग, या SMART ODR जहाँ लागू हो।" },
        { title: "दूसरे स्कैम से बचें", desc: "गारंटीड रिकवरी वालों को पैसा न दें। किसी भी सशुल्क परामर्श की अलग से जाँच करें।" },
      ],
      disclaimer: "CHINTAMAT कोई लॉ फर्म नहीं है। हम परिणाम की गारंटी नहीं देते। हम आधिकारिक भारतीय शिकायत प्रणालियों में मार्गदर्शन करते हैं।",
    },
    official: {
      title: "आधिकारिक सहायता लिंक",
      subtitle: "भारत के नियामक शिकायत पोर्टलों के सीधे लिंक — अपने नुकसान से मेल खाती अथॉरिटी चुनें। हर लिंक नए टैब में खुलता है।",
      items: [
        { title: "SEBI SCORES", desc: "रजिस्टर्ड संस्थाओं के विरुद्ध ट्रेडिंग व निवेश शिकायतें।", url: "https://scores.sebi.gov.in" },
        { title: "RBI CMS", desc: "बैंकिंग, UPI, कार्ड, लोन और NBFC शिकायतें।", url: "https://cms.rbi.org.in" },
        { title: "साइबर क्राइम पोर्टल", desc: "ऑनलाइन धोखाधड़ी और स्कैम भुगतान। हेल्पलाइन 1930।", url: "https://cybercrime.gov.in" },
        { title: "IRDAI Bima Bharosa", desc: "बीमा क्लेम और पॉलिसी विवाद।", url: "https://bimabharosa.irdai.gov.in" },
        { title: "AirSewa", desc: "एयरलाइन रिफंड, देरी, सामान और रद्दीकरण (DGCA)।", url: "https://airsewa.gov.in" },
        { title: "राष्ट्रीय उपभोक्ता हेल्पलाइन", desc: "उत्पाद व सेवा विवाद; उपभोक्ता आयोग।", url: "https://consumerhelpline.gov.in" },
        { title: "NHB", desc: "हाउसिंग फाइनेंस कंपनी शिकायतें।", url: "https://nhb.org.in" },
        { title: "PFRDA", desc: "NPS और पेंशन संबंधी शिकायतें।", url: "https://www.pfrda.org.in" },
      ],
    },
    report: {
      title: "गुमनाम रूप से रिपोर्ट करें",
      subtitle: "आपकी पहचान पूरी तरह गोपनीय रहती है। हर रिपोर्ट सार्वजनिक करने से पहले समीक्षा होती है।",
      scamType: "स्कैम का प्रकार",
      amountLost: "खोई हुई रकम (₹)",
      platform: "इस्तेमाल किया गया प्लेटफ़ॉर्म",
      telegram: "टेलीग्राम यूज़रनेम (यदि कोई)",
      upi: "UPI ID (यदि कोई)",
      phone: "फ़ोन नंबर (यदि कोई)",
      company: "कंपनी / सलाहकार का नाम",
      website: "वेबसाइट URL",
      description: "क्या हुआ — विस्तार से बताएँ",
      contact: "ईमेल (वैकल्पिक)",
      uploadEvidence: "सबूत अपलोड करें",
      uploadHelp: "स्क्रीनशॉट, PDF, भुगतान रसीद, चैट निर्यात। प्रति फ़ाइल 10MB तक।",
      submit: "सुरक्षित रूप से जमा करें",
      successTitle: "रिपोर्ट जमा हुई",
      successDesc: "धन्यवाद। हमारी टीम 72 घंटों में आपके सबूतों की समीक्षा करेगी। आपकी पहचान गोपनीय रहती है।",
      disclaimer: "हर आरोप के लिए सबूत आवश्यक है। झूठी या मानहानिकारक रिपोर्ट अस्वीकार की जा सकती हैं।",
      placeholders: {
        description: "तारीख, रकम, क्या वादा किया गया, और वास्तव में क्या हुआ — जितना विस्तार से, उतना बेहतर।",
      },
      types: [
        "टेलीग्राम प्रीमियम ग्रुप",
        "फर्जी SEBI सलाहकार",
        "रिसर्च एनालिस्ट / रिसर्च एडवायजरी",
        "अकाउंट हैंडलिंग धोखाधड़ी",
        "गारंटीड प्रॉफिट योजना",
        "व्हाट्सऐप सिग्नल पंप",
        "फर्जी प्रॉफिट स्क्रीनशॉट",
        "फॉरेक्स / क्रिप्टो रिकवरी स्कैम",
        "नकली कोर्स स्कैम",
        "अन्य",
      ],
    },
    database: {
      title: "स्कैमर डेटाबेस",
      subtitle: "रिपोर्ट किए गए स्कैमर्स का खोज योग्य डेटाबेस। टेलीग्राम, UPI, फ़ोन या कंपनी से खोजें।",
      searchPlaceholder: "टेलीग्राम, UPI, फ़ोन, कंपनी खोजें…",
      noResults: "हमारे डेटाबेस में कोई मिलान नहीं मिला।",
      victims: "पीड़ित",
      lossReported: "रिपोर्टेड नुकसान",
      statusLabels: {
        high_risk: "उच्च जोखिम",
        reported_cybercrime: "साइबर क्राइम में रिपोर्टेड",
        sebi_warning: "SEBI चेतावनी",
        multiple_complaints: "कई शिकायतें",
        under_investigation: "जाँच में",
      },
      filterAll: "सभी स्थितियाँ",
    },
    risk: {
      title: "स्कैम रिस्क चेकर",
      subtitle: "टेलीग्राम, UPI, फ़ोन या वेबसाइट दर्ज करके हमारे डेटाबेस में मिलान देखें।",
      telegram: "टेलीग्राम यूज़रनेम",
      upi: "UPI ID",
      phone: "फ़ोन नंबर",
      website: "वेबसाइट URL",
      check: "रिस्क जाँचें",
      verdictLabels: {
        DANGER: "खतरा — पुष्टि किया गया स्कैमर",
        "HIGH RISK": "उच्च जोखिम — कई शिकायतें",
        CAUTION: "सावधान — संभावित मिलान",
        "NO MATCH": "हमारे डेटाबेस में मिलान नहीं",
      },
      noMatchHelp: "मिलान न मिलना सुरक्षित होना नहीं है। रेड फ्लैग डिटेक्टर से व्यवहार भी जाँचें।",
    },
    redFlag: {
      title: "रेड फ्लैग डिटेक्टर",
      subtitle: "हर वो व्यवहार चुनें जो आपने देखा है। हम स्कैम संभावना स्कोर निकालेंगे।",
      checklist: [
        { key: "guaranteed_profits", label: "गारंटीड प्रॉफिट या निश्चित % रिटर्न का वादा" },
        { key: "fake_sebi_certificate", label: "SEBI सर्टिफिकेट दिखाते हैं लेकिन sebi.gov.in पर सत्यापन नहीं होता" },
        { key: "asks_remote_access", label: "रिमोट एक्सेस या ट्रेडिंग लॉगिन मांगते हैं" },
        { key: "asks_upi_to_personal", label: "व्यक्तिगत UPI / निजी खाते में भुगतान मांगते हैं" },
        { key: "pressure_tactics", label: "दबाव की तकनीक — 'सीमित सीटें', 'आज ही'" },
        { key: "fake_screenshots", label: "बिना ब्रोकर स्टेटमेंट के प्रॉफिट स्क्रीनशॉट" },
        { key: "no_written_agreement", label: "कोई लिखित अनुबंध नहीं, GST बिल नहीं" },
        { key: "unverified_advisor", label: "बिना सत्यापित प्रमाण के इन्फ्लुएंसर / एडमिन" },
      ],
      score: "स्कैम संभावना",
      flagged: "रेड फ्लैग पाए गए",
    },
    education: {
      eyebrow: "ज्ञान केंद्र",
      title: "जागरूकता केंद्र",
      subtitle: "भारतीय रिटेल ट्रेडरों के लिए स्कैम जागरूकता गाइड, रिकवरी प्रक्रिया और कानूनी जानकारी।",
      readMinutes: "मिनट पढ़ने में",
      filterAll: "सभी",
      emptyCategory: "इस श्रेणी में अभी कोई लेख नहीं।",
    },
    blog: {
      eyebrow: "जाँच · कहानियाँ · मार्गदर्शन",
      title: "ब्लॉग — सच्चे मामले, कानूनी मार्गदर्शन",
      subtitle: "सत्यापित पीड़ितों के अनुभव, कानूनी शिकायत चरण और सबक — भारत भर से।",
      allStories: "सभी कहानियाँ",
      featured: "विशेष",
      readFull: "पूरा पढ़ें",
      readArticle: "पूरा लेख पढ़ें",
      collapse: "संक्षिप्त करें",
      emptyCategory: "इस श्रेणी में अभी कोई लेख नहीं।",
      ctaTitle: "अपनी कहानी साझा करें?",
      ctaBody: "गुमनाम पीड़ित कहानियाँ हज़ारों लोगों की मदद करती हैं। अपनी रिपोर्ट भेजें — हम सत्यापन के बाद प्रकाशित करेंगे (पहचान गुप्त रहेगी)।",
      ctaButton: "केस जमा करें",
    },
    stories: {
      title: "सच्ची कहानियाँ",
      subtitle: "बैंकिंग धोखाधड़ी, बीमा विवाद, खरीदारी स्कैम, लोन ऐप और अन्य वित्तीय नुकसान के अनुभव — केवल ट्रेडिंग तक सीमित नहीं।",
      lossLabel: "नुकसान",
      lessonLabel: "मैंने क्या सीखा",
    },
    about: {
      title: "CHINTAMAT क्यों मौजूद है",
      subtitle:
        "भारतीय हर साल बैंकिंग धोखाधड़ी, साइबर स्कैम, बीमा mis-selling, उपभोक्ता विवाद और अपंजीकृत योजनाओं में हज़ारों करोड़ खो देते हैं। पीड़ितों को अक्सर पता नहीं होता किस अथॉरिटी पर जाएँ।",
      mission:
        "CHINTAMAT एक स्वतंत्र, सबूत-आधारित मंच है जो वित्तीय नुकसान से लड़ने में मदद करता है — सही शिकायत चैनल, सत्यापित केस फ़ाइल, और दोहराए जाने वाले अपराधियों का सार्वजनिक डेटाबेस।",
      pillars: [
        { title: "गोपनीयता", desc: "हर रिपोर्ट गुमनाम है, जब तक पीड़ित स्वयं पहचान साझा न करना चाहे।" },
        { title: "सबूत-आधारित", desc: "हम प्रोफ़ाइल और मार्गदर्शन तभी प्रकाशित करते हैं जब दावे सत्यापित दस्तावेज़ों से समर्थित हों।" },
        { title: "आधिकारिक मार्गदर्शन", desc: "हम RBI, SEBI, IRDAI, साइबर क्राइम, उपभोक्ता और अन्य नियामक पोर्टल की ओर ले जाते हैं — अप्रमाणित रिकवरी एजेंटों की नहीं।" },
      ],
      notDo: {
        title: "CHINTAMAT क्या नहीं करता",
        items: [
          "निवेश टिप, ट्रेडिंग सिग्नल, या गारंटीड रिटर्न सलाह नहीं देता।",
          "अप्रमाणित कोर्स नहीं बेचता और न ब्रोकर/एजेंट के रूप में काम करता है।",
          "किसी का बैंक, डीमैट या ट्रेडिंग खाता एक्सेस या प्रबंधित नहीं करता।",
          "रकम वापसी की गारंटी नहीं देता और न कानूनी फर्म या पुलिस के रूप में पेश होता है।",
          "जिन संस्थाओं की जाँच करता है उनसे paid promotion नहीं लेता।",
        ],
      },
      disclaimer: "CHINTAMAT कोई लॉ फर्म या वित्तीय सलाहकार नहीं है। हम आधिकारिक भारतीय शिकायत प्रणालियों की ओर मार्गदर्शन करते हैं।",
    },
    contact: {
      title: "संपर्क करें",
      subtitle: "ईमेल सबसे सुरक्षित माध्यम है। सुरक्षा कारणों से फ़ोन समर्थन उपलब्ध नहीं है।",
      name: "आपका नाम (या उपनाम)",
      email: "ईमेल",
      subject: "विषय",
      message: "संदेश",
      send: "संदेश भेजें",
      typeGeneral: "सामान्य पूछताछ",
      typeMedia: "मीडिया / प्रेस",
      typeAnon: "गुमनाम सूचना",
      success: "संदेश भेज दिया गया। 3-5 कार्य दिवसों में उत्तर देंगे।",
    },
    footer: {
      tagline: "सुनी-सुनाई बातों पर नहीं, सबूतों पर। चुप्पी पर नहीं, सुरक्षा पर।",
      sections: {
        platform: "प्लेटफ़ॉर्म",
        legal: "कानूनी",
        connect: "जुड़ें",
      },
      links: {
        privacy: "गोपनीयता नीति",
        terms: "उपयोग की शर्तें",
        disclaimer: "अस्वीकरण",
        antiHarassment: "उत्पीड़न-विरोधी नीति",
        dataDeletion: "डेटा हटाने का अनुरोध",
      },
      disclaimer:
        "CHINTAMAT कोई वित्तीय सलाह नहीं देता। हम रकम वापसी की गारंटी नहीं देते। हर आरोप के लिए सबूत आवश्यक।",
      rights: "© 2026 CHINTAMAT. एक जन-हित एंटी-स्कैम मिशन।",
    },
    stats: {
      reports: "रिपोर्ट सत्यापित",
      scammers: "स्कैमर ट्रैक",
      cases: "हल किए केस",
      loss: "नुकसान रिपोर्टेड",
    },
  },

  te: {
    nav: {
      home: "హోమ్",
      database: "మోసగాళ్ల డేటాబేస్",
      recovery: "రికవరీ ప్రాసెస్",
      risk: "రిస్క్ చెకర్",
      redFlags: "రెడ్ ఫ్లాగ్స్",
      education: "అవగాహన",
      stories: "నిజమైన కథలు",
      about: "మా గురించి",
      contact: "సంప్రదించండి",
      legalHelp: "చట్టపరమైన సహాయం",
      reportCta: "మోసం రిపోర్ట్",
      mentors: "మెంటార్లు",
      dashboard: "డ్యాష్‌బోర్డ్",
      signIn: "సైన్ ఇన్",
      signOut: "సైన్ అవుట్",
    },
    common: {
      reportScam: "మోసం రిపోర్ట్ చేయండి",
      learnRecovery: "రికవరీ ప్రాసెస్ నేర్చుకోండి",
      confidential: "100% గోప్యం",
      readMore: "మరింత చదవండి",
      backHome: "హోమ్‌కు తిరిగి వెళ్లండి",
      search: "శోధించండి",
      verified: "ధృవీకరించబడింది",
      anonymous: "అజ్ఞాత",
      submit: "సమర్పించండి",
      cancel: "రద్దు చేయండి",
      requiredField: "అవసరం",
      check: "ఇప్పుడే తనిఖీ చేయండి",
      loading: "లోడ్ అవుతోంది…",
      error: "ఏదో తప్పు జరిగింది. మళ్ళీ ప్రయత్నించండి.",
    },
    hero: {
      eyebrow: "భారతదేశ సాక్ష్యం-ఆధారిత ఆర్థిక ఫిర్యాదు మార్గదర్శన ప్లాట్‌ఫారమ్",
      title1: "ఆర్థిక నష్టానికి",
      title2: "వ్యతిరేకంగా పోరాడండి.",
      subtitle:
        "ట్రేడింగ్ మోసం నుండి బ్యాంకింగ్, సైబర్, భీమా, విమాన, వినియోగదారు, హౌసింగ్ ఫైనాన్స్ మరియు పెన్షన్ వివాదాల వరకు — సరైన అధికారాన్ని కనుగొనడంలో, సరైన సాక్ష్యాన్ని సిద్ధం చేయడంలో మరియు అధికారిక భారతీయ ఫిర్యాదు వ్యవస్థల ద్వారా సరైన తదుపరి అడుగు వేయడంలో మేము మీకు సహాయం చేస్తాము.",
      reportBtn: "ఆర్థిక నష్టాన్ని రిపోర్ట్ చేయండి",
      recoveryBtn: "ఫిర్యాదు ప్రక్రియ నేర్చుకోండి",
      taglines: [
        "సరైన అధికారాన్ని కనుగొనండి.",
        "సరైన సాక్ష్యాన్ని సిద్ధం చేయండి.",
        "సరైన తదుపరి అడుగు వేయండి.",
      ],
    },
    trust: {
      title: "నమ్మకంపై నిర్మించబడింది, సాక్ష్యంతో మద్దతు",
      items: [
        { title: "100% గోప్యం", desc: "రిపోర్ట్‌లు డిఫాల్ట్‌గా అజ్ఞాతంగా ఉంటాయి. మేము మీ గుర్తింపును ఎప్పుడూ బహిర్గతం చేయము." },
        { title: "సాక్ష్యం-మొదట", desc: "ప్రతి ఫిర్యాదు ధృవీకరించబడిన ఆధారాలపై నిర్మించబడుతుంది, వినికిడిపై కాదు." },
        { title: "నిజాయితీ మార్గదర్శనం", desc: "అధికారిక ఫిర్యాదు మార్గాలను స్పష్టంగా వివరిస్తాము — తప్పుడు వాగ్దానాలు లేకుండా." },
        { title: "అధికారిక ఛానెల్‌లు మాత్రమే", desc: "RBI, SEBI, IRDAI, వినియోగదారు ఫోరమ్‌లు మరియు ఇతర నియంత్రణ సంస్థలకు మార్గనిర్దేశం." },
      ],
    },
    scams: {
      title: "ఆర్థిక నష్టాలు ఎక్కడ జరుగుతాయి",
      subtitle: "బ్యాంకింగ్, డిజిటల్ చెల్లింపులు, భీమా, షాపింగ్, లోన్లు, పెట్టుబడులు లేదా మోసం ద్వారా నష్టం రావచ్చు. డబ్బు కదలేముందే నమూనాలను గుర్తించండి.",
      categories: [
        { key: "upi_fraud", title: "UPI & డిజిటల్ చెల్లింపు మోసం", desc: "నకిలీ లింక్‌లు, QR మార్పిడి, తప్పు ఖాతాకు బదిలీ.", icon: "Send" },
        { key: "phishing", title: "ఫిషింగ్ & మోసపు గుర్తింపు", desc: "బ్యాంక్, RBI, పోలీసు అధికారులుగా OTP చోరీ.", icon: "BadgeCheck" },
        { key: "unauth_debit", title: "అనధికార డెబిట్‌లు", desc: "అకస్మాత్తుగా కార్డు/UPI డెబిట్, mandate దుర్వినియోగం.", icon: "KeyRound" },
        { key: "insurance", title: "భీమా mis-selling", desc: "పెట్టుబడిగా అమ్మిన పాలసీలు, దాచిన ఛార్జీలు.", icon: "TrendingUp" },
        { key: "consumer", title: "ఆన్‌లైన్ షాపింగ్ & రీఫండ్ మోసం", desc: "చెల్లించినా డెలివరీ లేదు, రీఫండ్ నిరాకరణ.", icon: "MessageSquare" },
        { key: "loan_app", title: "అత్యధిక వడ్డీ లోన్ యాప్‌లు", desc: "వేధింపులు, దాచిన ఫీజులు, డేటా బ్లాక్‌మెయిల్.", icon: "Image" },
        { key: "recovery", title: "నకిలీ రికవరీ ఏజెంట్లు", desc: "ఇప్పటికే పోగొట్టుకున్న డబ్బును తిరిగి ఇస్తామని ఫీజు అడుగుతారు.", icon: "RefreshCcw" },
        { key: "investment", title: "నమోదు కాని పెట్టుబడి పథకాలు", desc: "చిట్ ఫండ్‌లు, MLM, క్రిప్టో — చట్టబద్ధమైన లైసెన్స్ లేకుండా.", icon: "BookOpen" },
      ],
    },
    recovery: {
      title: "ఫిర్యాదు పరిష్కారం ఎలా పనిచేస్తుంది",
      subtitle: "భారతదేశంలో ఏ ఆర్థిక నష్టానికైనా — డాక్యుమెంట్, సరైన అధికారానికి దాఖలు, ఫాలో-అప్ మరియు ఎస్కలేషన్.",
      steps: [
        { title: "అన్నీ డాక్యుమెంట్ చేయండి", desc: "రసీదులు, UPI రెఫరెన్స్‌లు, ఇన్‌వాయిస్‌లు, ఒప్పందాలు, చాట్ ఎగుమతులు." },
        { title: "ఖాతాలను సురక్షితం చేయండి", desc: "పాస్‌వర్డ్‌లు మార్చండి, కార్డ్‌లు బ్లాక్ చేయండి, బ్యాంక్‌కు వెంటనే తెలియజేయండి." },
        { title: "సరైన అధికారాన్ని ఎంచుకోండి", desc: "బ్యాంకింగ్ → RBI CMS; సైబర్ → cybercrime.gov.in; భీమా → IRDAI; విమాన → AirSewa." },
        { title: "అధికారిక పోర్టల్‌లో దాఖలు", desc: "నియంత్రక వెబ్‌సైట్‌లో ఫిర్యాదు — సాక్ష్యాలు, వాస్తవిక కాలక్రమం." },
        { title: "ప్రతి రెఫరెన్స్ నంబర్ ఉంచండి", desc: "ఫిర్యాదు ID, FIR, హెల్ప్‌లైన్ టికెట్ — ఫాలో-అప్‌కు అవసరం." },
        { title: "ఫాలో-అప్ & ఎస్కలేట్", desc: "స్పందన లేకపోతే ombudsman, వినియోగదారు కమిషన్, SMART ODR." },
        { title: "రెండవ మోసం నుండి జాగ్రత్త", desc: "తప్పనిసరి recovery కోసం డబ్బు చెల్లించవద్దు. చెల్లింపు సలహా వేరుగా ధృవీకరించండి." },
      ],
      disclaimer: "CHINTAMAT ఒక లా ఫర్మ్ కాదు. మేము ఫలితాలకు హామీ ఇవ్వము.",
    },
    official: {
      title: "అధికారిక సహాయ లింకులు",
      subtitle: "భారత నియంత్రణ ఫిర్యాదు పోర్టళ్లకు నేరుగా లింకులు — మీ నష్టానికి సరిపోయే అధికారాన్ని ఎంచుకోండి. ప్రతి లింక్ కొత్త ట్యాబ్‌లో తెరుచుకుంటుంది.",
      items: [
        { title: "SEBI SCORES", desc: "రిజిస్టర్డ్ ఇంటర్మీడియరీలకు వ్యతిరేకంగా ట్రేడింగ్ & పెట్టుబడి ఫిర్యాదులు.", url: "https://scores.sebi.gov.in" },
        { title: "RBI CMS", desc: "బ్యాంకింగ్, UPI, కార్డు, లోన్ & NBFC ఫిర్యాదులు.", url: "https://cms.rbi.org.in" },
        { title: "సైబర్ క్రైమ్ పోర్టల్", desc: "ఆన్‌లైన్ మోసం & స్కామ్ చెల్లింపులు. హెల్ప్‌లైన్ 1930.", url: "https://cybercrime.gov.in" },
        { title: "IRDAI Bima Bharosa", desc: "భీమా క్లెయిమ్ & పాలసీ వివాదాలు.", url: "https://bimabharosa.irdai.gov.in" },
        { title: "AirSewa", desc: "విమాన రీఫండ్, ఆలస్యం, సామాను & రద్దు సమస్యలు (DGCA).", url: "https://airsewa.gov.in" },
        { title: "జాతీయ వినియోగదారు హెల్ప్‌లైన్", desc: "ఉత్పత్తి & సేవ వివాదాలు; వినియోగదారు కమిషన్.", url: "https://consumerhelpline.gov.in" },
        { title: "NHB", desc: "హౌసింగ్ ఫైనాన్స్ కంపెనీ ఫిర్యాదులు.", url: "https://nhb.org.in" },
        { title: "PFRDA", desc: "NPS & పెన్షన్ సంబంధిత ఫిర్యాదులు.", url: "https://www.pfrda.org.in" },
      ],
    },
    report: {
      title: "అజ్ఞాతంగా మోసాన్ని రిపోర్ట్ చేయండి",
      subtitle: "మీ గుర్తింపు ఖచ్చితంగా గోప్యంగా ఉంచబడుతుంది. అన్ని సమర్పణలు సమీక్షించబడతాయి.",
      scamType: "మోసం రకం",
      amountLost: "పోగొట్టుకున్న మొత్తం (₹)",
      platform: "ఉపయోగించిన ప్లాట్‌ఫారమ్",
      telegram: "టెలిగ్రామ్ యూజర్‌నేమ్",
      upi: "UPI ID",
      phone: "ఫోన్ నంబర్",
      company: "కంపెనీ / సలహాదారు పేరు",
      website: "వెబ్‌సైట్ URL",
      description: "ఏమి జరిగిందో వివరించండి",
      contact: "ఇమెయిల్ (ఐచ్ఛికం)",
      uploadEvidence: "సాక్ష్యాన్ని అప్‌లోడ్ చేయండి",
      uploadHelp: "స్క్రీన్‌షాట్‌లు, PDFs, చెల్లింపు రుజువులు. ఫైల్‌కు గరిష్టంగా 10MB.",
      submit: "సురక్షితంగా రిపోర్ట్ సమర్పించండి",
      successTitle: "రిపోర్ట్ సమర్పించబడింది",
      successDesc: "ధన్యవాదాలు. మా బృందం 72 గంటల్లో సమీక్షిస్తుంది. మీ గుర్తింపు గోప్యంగా ఉంటుంది.",
      disclaimer: "అన్ని ఆరోపణలకు సాక్ష్యం అవసరం. తప్పుడు సమర్పణలు తిరస్కరించబడవచ్చు.",
      placeholders: {
        description: "తేదీలు, మొత్తాలు, వాగ్దానాలు మరియు వాస్తవంగా ఏమి జరిగిందో చేర్చండి.",
      },
      types: [
        "టెలిగ్రామ్ ప్రీమియం గ్రూప్",
        "నకిలీ SEBI సలహాదారు",
        "రీసెర్చ్ అనలిస్ట్ / రీసెర్చ్ అడ్వైజరీ",
        "ఖాతా నిర్వహణ మోసం",
        "గ్యారెంటీడ్ లాభ పథకం",
        "వాట్సాప్ సిగ్నల్ పంప్",
        "నకిలీ లాభ స్క్రీన్‌షాట్‌లు",
        "ఫారెక్స్ / క్రిప్టో రికవరీ మోసం",
        "నకిలీ కోర్సు మోసం",
        "ఇతరం",
      ],
    },
    database: {
      title: "మోసగాళ్ల డేటాబేస్",
      subtitle: "శోధించదగిన, సాక్ష్యం-ఆధారిత డేటాబేస్. టెలిగ్రామ్, UPI, ఫోన్ లేదా కంపెనీ ద్వారా శోధించండి.",
      searchPlaceholder: "టెలిగ్రామ్, UPI, ఫోన్, కంపెనీ శోధించండి…",
      noResults: "మా డేటాబేస్‌లో సరిపోలికలు లేవు.",
      victims: "బాధితులు",
      lossReported: "నష్టం రిపోర్ట్",
      statusLabels: {
        high_risk: "అధిక ప్రమాదం",
        reported_cybercrime: "సైబర్ క్రైమ్‌కు రిపోర్ట్",
        sebi_warning: "SEBI హెచ్చరిక",
        multiple_complaints: "అనేక ఫిర్యాదులు",
        under_investigation: "దర్యాప్తులో",
      },
      filterAll: "అన్ని స్థితులు",
    },
    risk: {
      title: "మోసం రిస్క్ చెకర్",
      subtitle: "మా రిపోర్ట్ చేయబడిన మోసగాళ్ల డేటాబేస్‌కు వ్యతిరేకంగా తనిఖీ చేయడానికి దర్యాప్తు చేయండి.",
      telegram: "టెలిగ్రామ్ యూజర్‌నేమ్",
      upi: "UPI ID",
      phone: "ఫోన్ నంబర్",
      website: "వెబ్‌సైట్ URL",
      check: "రిస్క్ తనిఖీ చేయండి",
      verdictLabels: {
        DANGER: "ప్రమాదం — ధృవీకరించబడిన మోసగాడు",
        "HIGH RISK": "అధిక ప్రమాదం — అనేక ఫిర్యాదులు",
        CAUTION: "జాగ్రత్త — సాధ్యమైన సరిపోలిక",
        "NO MATCH": "మా డేటాబేస్‌లో సరిపోలిక లేదు",
      },
      noMatchHelp: "సరిపోలిక లేకపోవడం సురక్షితమని కాదు. ప్రవర్తనను అంచనా వేయడానికి రెడ్ ఫ్లాగ్ డిటెక్టర్ ఉపయోగించండి.",
    },
    redFlag: {
      title: "రెడ్ ఫ్లాగ్ డిటెక్టర్",
      subtitle: "మీరు గమనించిన ప్రతి ప్రవర్తనను టిక్ చేయండి. మేము స్కామ్ సంభావ్యత స్కోర్‌ను లెక్కిస్తాము.",
      checklist: [
        { key: "guaranteed_profits", label: "గ్యారెంటీడ్ లాభాలు లేదా స్థిర % రాబడిని వాగ్దానం చేస్తుంది" },
        { key: "fake_sebi_certificate", label: "SEBI సర్టిఫికెట్ చూపిస్తుంది కానీ sebi.gov.in‌లో ధృవీకరించలేరు" },
        { key: "asks_remote_access", label: "రిమోట్ యాక్సెస్ లేదా మీ ట్రేడింగ్ లాగిన్ అడుగుతుంది" },
        { key: "asks_upi_to_personal", label: "వ్యక్తిగత UPI / ఖాతాకు చెల్లించమని అడుగుతుంది" },
        { key: "pressure_tactics", label: "ఒత్తిడి వ్యూహాలు — 'పరిమిత స్లాట్‌లు', 'నేటి మాత్రమే'" },
        { key: "fake_screenshots", label: "బ్రోకర్ స్టేట్‌మెంట్ లేకుండా లాభ స్క్రీన్‌షాట్‌లు చూపిస్తుంది" },
        { key: "no_written_agreement", label: "వ్రాతపూర్వక ఒప్పందం లేదు, GST ఇన్‌వాయిస్ లేదు" },
        { key: "unverified_advisor", label: "ధృవీకరించలేని ఆధారాలు ఉన్న ఇన్ఫ్లుయెన్సర్ / టెలిగ్రామ్ అడ్మిన్" },
      ],
      score: "మోసం సంభావ్యత",
      flagged: "రెడ్ ఫ్లాగ్‌లు గుర్తించబడ్డాయి",
    },
    education: {
      eyebrow: "జ్ఞాన కోషం",
      title: "అవగాహన కేంద్రం",
      subtitle: "భారతీయ రిటైల్ ట్రేడర్ల కోసం స్కామ్ అవగాహన గైడ్‌లు, రికవరీ వాక్‌త్రూలు మరియు చట్టపరమైన వివరణలు.",
      readMinutes: "నిమి. చదవడానికి",
      filterAll: "అన్నీ",
      emptyCategory: "ఈ వర్గంలో ఇంకా వ్యాసాలు లేవు.",
    },
    blog: {
      eyebrow: "పరిశోధన · కథలు · మార్గదర్శకాలు",
      title: "బ్లాగ్ — నిజ కేసులు, చట్టపరమైన మార్గదర్శనం",
      subtitle: "ధృవీకరించబడిన బాధితుల అనుభవాలు మరియు చట్టపరమైన ఫైలింగ్ దశలు.",
      allStories: "అన్ని కథలు",
      featured: "విశేషం",
      readFull: "పూర్తిగా చదవండి",
      readArticle: "పూర్తి వ్యాసం",
      collapse: "మూసివేయి",
      emptyCategory: "ఈ వర్గంలో ఇంకా వ్యాసాలు లేవు.",
      ctaTitle: "మీ కథ ఉందా?",
      ctaBody: "అజ్ఞాత బాధిత కథలు వేలాది మందికి సహాయం చేస్తాయి. మీ కేసును పంపండి — మేము ధృవీకరించి ప్రచురిస్తాము.",
      ctaButton: "కేసు సమర్పించండి",
    },
    stories: {
      title: "నిజమైన బాధితుల కథలు",
      subtitle: "బ్యాంకింగ్ మోసం, భీమా వివాదాలు, షాపింగ్ స్కామ్‌లు, లోన్ యాప్‌లు — ట్రేడింగ్‌కు పరిమితం కాని ఆర్థిక నష్ట అనుభవాలు.",
      lossLabel: "నష్టం",
      lessonLabel: "నేను నేర్చుకున్నది",
    },
    about: {
      title: "CHINTAMAT ఎందుకు ఉంది",
      subtitle:
        "భారతీయులు ప్రతి సంవత్సరం బ్యాంకింగ్ మోసం, సైబర్ స్కామ్‌లు, భీమా mis-selling, వినియోగదారు వివాదాలలో వేల కోట్లు కోల్పోతున్నారు.",
      mission:
        "CHINTAMAT స్వతంత్ర, సాక్ష్యం-ఆధారిత వేదిక — సరైన ఫిర్యాదు చానెల్, ధృవీకరించిన కేస్ ఫైల్, పబ్లిక్ డేటాబేస్ ద్వారా ఆర్థిక నష్టానికి పోరాడడంలో సహాయం.",
      pillars: [
        { title: "గోప్యత", desc: "బాధితుడు స్వయంగా గుర్తించబడాలని ఎంచుకుంటే తప్ప అన్ని రిపోర్ట్‌లు అజ్ఞాతం." },
        { title: "సాక్ష్యం-మద్దతు", desc: "ధృవీకరించిన ఆధారాల మద్దతు ఉన్నప్పుడు మాత్రమే ప్రొఫైల్‌లు." },
        { title: "అధికారిక మార్గదర్శనం", desc: "RBI, SEBI, IRDAI, సైబర్ క్రైమ్, వినియోగదారు పోర్టల్‌లకు — అప్రమాణిత recovery ఏజెంట్లకు కాదు." },
      ],
      notDo: {
        title: "CHINTAMAT చేయనిది",
        items: [
          "పెట్టుబడి టిప్‌లు, ట్రేడింగ్ సిగ్నల్‌లు లేదా guaranteed returns.",
          "అప్రమాణిత కోర్సులు అమ్మదు; బ్రోకర్/ఏజెంట్‌గా పని చేయదు.",
          "ఎవరి బ్యాంక్, డీమ్యాట్ లేదా ట్రేడింగ్ ఖాతాను నిర్వహించదు.",
          "నిధుల రికవరీకి హామీ ఇవ్వదు; లా ఫర్మ్/పోలీసు గా నటించదు.",
          "తనిఖీ చేసే సంస్థల నుండి paid promotion అంగీకరించదు.",
        ],
      },
      disclaimer: "CHINTAMAT లా ఫర్మ్ లేదా ఆర్థిక సలహాదారు కాదు. అధికారిక భారతీయ ఫిర్యాదు వ్యవస్థలకు మార్గనిర్దేశం.",
    },
    contact: {
      title: "సంప్రదించండి",
      subtitle: "ఇమెయిల్ ప్రాధాన్యత మరియు అత్యంత సురక్షితమైన ఛానెల్. ఫోన్ మద్దతు అందించబడదు.",
      name: "మీ పేరు (లేదా మారుపేరు)",
      email: "ఇమెయిల్",
      subject: "విషయం",
      message: "సందేశం",
      send: "సందేశం పంపండి",
      typeGeneral: "సాధారణ విచారణ",
      typeMedia: "మీడియా / ప్రెస్",
      typeAnon: "అజ్ఞాత చిట్కా",
      success: "సందేశం పంపబడింది. మేము 3-5 పనిదినాల్లో స్పందిస్తాము.",
    },
    footer: {
      tagline: "విన్నవాటికి కాదు, సాక్ష్యానికి. మౌనానికి కాదు, రక్షణకు.",
      sections: { platform: "ప్లాట్‌ఫారమ్", legal: "చట్టపరమైనది", connect: "కనెక్ట్" },
      links: {
        privacy: "గోప్యతా విధానం",
        terms: "ఉపయోగ నిబంధనలు",
        disclaimer: "నిరాకరణ",
        antiHarassment: "హింస-వ్యతిరేక విధానం",
        dataDeletion: "డేటా తొలగింపు అభ్యర్థన",
      },
      disclaimer:
        "CHINTAMAT ఆర్థిక సలహా అందించదు. మేము నిధుల రికవరీకి హామీ ఇవ్వము. అన్ని ఆరోపణలకు సాక్ష్యం అవసరం.",
      rights: "© 2026 CHINTAMAT. ప్రజా-ప్రయోజన యాంటీ-స్కామ్ మిషన్.",
    },
    stats: {
      reports: "రిపోర్ట్‌లు ధృవీకరించబడ్డాయి",
      scammers: "ట్రాక్ చేయబడిన మోసగాళ్లు",
      cases: "పరిష్కరించబడిన కేసులు",
      loss: "నష్టం రిపోర్ట్",
    },
  },

  ta: {
    nav: {
      home: "முகப்பு",
      database: "மோசடி தரவுத்தளம்",
      recovery: "மீட்பு செயல்முறை",
      risk: "ஆபத்து சரிபார்ப்பு",
      redFlags: "சிவப்பு கொடிகள்",
      education: "விழிப்புணர்வு",
      stories: "உண்மையான கதைகள்",
      about: "எங்களைப் பற்றி",
      contact: "தொடர்பு",
      legalHelp: "சட்ட உதவி",
      reportCta: "புகாரளி",
      mentors: "வழிகாட்டிகள்",
      dashboard: "டாஷ்போர்டு",
      signIn: "உள்நுழை",
      signOut: "வெளியேறு",
    },
    common: {
      reportScam: "மோசடியை புகாரளி",
      learnRecovery: "மீட்பு செயல்முறையை அறிய",
      confidential: "100% இரகசியம்",
      readMore: "மேலும் படிக்க",
      backHome: "முகப்பிற்குத் திரும்பு",
      search: "தேடு",
      verified: "சரிபார்க்கப்பட்டது",
      anonymous: "அநாமதேய",
      submit: "சமர்ப்பி",
      cancel: "ரத்து செய்",
      requiredField: "தேவை",
      check: "இப்போது சரிபார்",
      loading: "ஏற்றுகிறது…",
      error: "ஏதோ தவறு நடந்தது. மீண்டும் முயற்சிக்கவும்.",
    },
    hero: {
      eyebrow: "இந்தியாவின் சான்று-அடிப்படையிலான நிதி புகார் வழிகாட்டுதல் தளம்",
      title1: "நிதி இழப்பை",
      title2: "எதிர்த்து போராடுங்கள்.",
      subtitle:
        "வர்த்தக மோசடி முதல் வங்கி, சைபர், காப்பீடு, விமானம், நுகர்வோர், வீட்டு நிதி மற்றும் ஓய்வூதிய சர்ச்சைகள் வரை — சரியான அதிகாரத்தைக் கண்டறியவும், சரியான சான்றுகளைத் தயாரிக்கவும், அதிகாரப்பூர்வ இந்திய புகார் அமைப்புகள் மூலம் சரியான அடுத்த படியை எடுக்கவும் நாங்கள் உதவுகிறோம்.",
      reportBtn: "நிதி இழப்பைப் புகாரளி",
      recoveryBtn: "புகார் செயல்முறையை அறிய",
      taglines: [
        "சரியான அதிகாரத்தைக் கண்டறியுங்கள்.",
        "சரியான சான்றுகளைத் தயாரியுங்கள்.",
        "சரியான அடுத்த படியை எடுங்கள்.",
      ],
    },
    trust: {
      title: "நம்பிக்கையின் மீது கட்டப்பட்டது, சான்றுகளால் ஆதரிக்கப்படுகிறது",
      items: [
        { title: "100% இரகசியம்", desc: "புகார்கள் இயல்பாகவே அநாமதேயமானவை. உங்கள் அடையாளத்தை வெளிப்படுத்த மாட்டோம்." },
        { title: "சான்று-முதலில்", desc: "ஒவ்வொரு புகாரும் சரிபார்க்கக்கூடிய ஆதாரத்தின் மீது கட்டப்படுகிறது, வதந்தியின் மீது அல்ல." },
        { title: "நேர்மையான வழிகாட்டுதல்", desc: "அதிகாரப்பூர்வ புகார் வழிகளை தெளிவாக விளக்குகிறோம் — தவறான வாக்குறுதிகள் இல்லாமல்." },
        { title: "அதிகாரப்பூர்வ சேனல்கள் மட்டும்", desc: "RBI, SEBI, IRDAI, நுகர்வோர் மன்றங்கள் மற்றும் அரசு ஒழுங்குமுறையாளர்களுக்கு வழிநடத்துகிறோம்." },
      ],
    },
    scams: {
      title: "நிதி இழப்புகள் எங்கே நடக்கின்றன",
      subtitle: "வங்கி, டிஜிட்டல் பணம், காப்பீடு, ஷாப்பிங், கடன், முதலீடு அல்லது மோசடியில் இழப்பு ஏற்படலாம். பணம் செல்வதற்கு முன் வடிவங்களை அடையாளம் காணுங்கள்.",
      categories: [
        { key: "upi_fraud", title: "UPI & டிஜிட்டல் பண மோசடி", desc: "போலி இணைப்புகள், QR மாற்றம், தவறான கணக்கிற்கு பரிமாற்றம்.", icon: "Send" },
        { key: "phishing", title: "பிஷிங் & மோசடி அடையாளம்", desc: "வங்கி, RBI, போலீஸ் அதிகாரிகளாக OTP திருடுதல்.", icon: "BadgeCheck" },
        { key: "unauth_debit", title: "அங்கீகரிக்கப்படாத வங்கி டெபிட்", desc: "திடீர் கார்டு/UPI டெபிட், mandate தவறான பயன்பாடு.", icon: "KeyRound" },
        { key: "insurance", title: "காப்பீடு தவறான விற்பனை", desc: "முதலீடாக விற்கப்பட்ட பாலிசிகள், மறைக்கப்பட்ட கட்டணங்கள்.", icon: "TrendingUp" },
        { key: "consumer", title: "ஆன்லைன் ஷாப்பிங் & பணத்திருப்பு மோசடி", desc: "பணம் செலுத்தியும் விநியோகம் இல்லை, பணத்திருப்பு மறுப்பு.", icon: "MessageSquare" },
        { key: "loan_app", title: "அதிக வட்டி கடன் செயலிகள்", desc: "துன்புறுத்தல், மறைக்கப்பட்ட கட்டணம், தரவு பிளாக்மெயில்.", icon: "Image" },
        { key: "recovery", title: "போலி மீட்பு முகவர்கள்", desc: "ஏற்கனவே இழந்த பணத்தை மீட்டுத் தருவதாக முன்பணம் கேட்பவர்கள்.", icon: "RefreshCcw" },
        { key: "investment", title: "பதிவில்லாத முதலீட்டு திட்டங்கள்", desc: "சிட் ஃபண்ட், MLM, கிரிப்டோ — சட்டப்பூர்வ உரிமம் இல்லாமல்.", icon: "BookOpen" },
      ],
    },
    recovery: {
      title: "புகார் தீர்வு எவ்வாறு செயல்படுகிறது",
      subtitle: "இந்தியாவில் எந்த நிதி இழப்புக்கும் — ஆவணப்படுத்தல், சரியான அதிகாரத்தில் தாக்கல், பின்தொடர்தல் மற்றும் மேல்முறை.",
      steps: [
        { title: "அனைத்தையும் ஆவணப்படுத்துங்கள்", desc: "ரசீதுகள், UPI குறிப்புகள், விலைப்பட்டியல், ஒப்பந்தங்கள், அரட்டை ஏற்றுமதிகள்." },
        { title: "கணக்குகளை பாதுகாக்கவும்", desc: "கடவுச்சொற்களை மாற்றுங்கள், கார்டுகளை தடுங்கள், வங்கியை உடனடியாக அறிவிக்கவும்." },
        { title: "சரியான அதிகாரத்தை தேர்ந்தெடுக்கவும்", desc: "வங்கி → RBI CMS; சைபர் → cybercrime.gov.in; காப்பீடு → IRDAI; விமான → AirSewa." },
        { title: "அதிகாரப்பூர்வ போர்டலில் தாக்கல்", desc: "ஒழுங்குமுறை வலைத்தளத்தில் புகார் — ஆதாரங்கள், உண்மை நிகழ்வு வரிசை." },
        { title: "எல்லா குறிப்பு எண்களையும் வைத்திருங்கள்", desc: "புகார் ID, FIR, ஹெல்ப்லைன் டிக்கெட் — பின்தொடர்தலுக்கு தேவை." },
        { title: "பின்தொடர்தல் & மேல்முறை", desc: "பதில் இல்லையெனில் ombudsman, நுகர்வோர் ஆணையம், SMART ODR." },
        { title: "இரண்டாம் மோசடியை தவிர்க்கவும்", desc: "guaranteed recovery க்கு பணம் செலுத்த வேண்டாம். கட்டண ஆலோசனையை தனியாக சரிபார்க்கவும்." },
      ],
      disclaimer: "CHINTAMAT ஒரு சட்ட நிறுவனம் அல்ல. நாங்கள் முடிவுகளுக்கு உத்தரவாதம் அளிக்கவில்லை.",
    },
    official: {
      title: "அதிகாரப்பூர்வ உதவி இணைப்புகள்",
      subtitle: "இந்தியாவின் ஒழுங்குமுறை புகார் போர்டல்களுக்கான நேரடி இணைப்புகள் — உங்கள் இழப்புக்கு பொருந்தும் அதிகாரத்தைத் தேர்ந்தெடுங்கள். ஒவ்வொன்றும் புதிய தாவலில் திறக்கும்.",
      items: [
        { title: "SEBI SCORES", desc: "பதிவுசெய்யப்பட்ட இடைத்தரகர்களுக்கு எதிரான வர்த்தக & முதலீட்டு புகார்கள்.", url: "https://scores.sebi.gov.in" },
        { title: "RBI CMS", desc: "வங்கி, UPI, கார்டு, கடன் & NBFC புகார்கள்.", url: "https://cms.rbi.org.in" },
        { title: "சைபர் கிரைம் போர்டல்", desc: "ஆன்லைன் மோசடி & மோசடி பணம். ஹெல்ப்லைன் 1930.", url: "https://cybercrime.gov.in" },
        { title: "IRDAI Bima Bharosa", desc: "காப்பீட்டு உரிமைகோரல் & பாலிசி சர்ச்சைகள்.", url: "https://bimabharosa.irdai.gov.in" },
        { title: "AirSewa", desc: "விமான பணத்திருப்பம், தாமதம், பயணப்பை & ரத்து சிக்கல்கள் (DGCA).", url: "https://airsewa.gov.in" },
        { title: "தேசிய நுகர்வோர் ஹெல்ப்லைன்", desc: "பொருள் & சேவை சர்ச்சைகள்; நுகர்வோர் ஆணையம்.", url: "https://consumerhelpline.gov.in" },
        { title: "NHB", desc: "வீட்டு நிதி நிறுவன புகார்கள்.", url: "https://nhb.org.in" },
        { title: "PFRDA", desc: "NPS & ஓய்வூதிய தொடர்பான புகார்கள்.", url: "https://www.pfrda.org.in" },
      ],
    },
    report: {
      title: "மோசடியை அநாமதேயமாக புகாரளி",
      subtitle: "உங்கள் அடையாளம் கண்டிப்பாக இரகசியமாக வைக்கப்படுகிறது.",
      scamType: "மோசடி வகை",
      amountLost: "இழந்த தொகை (₹)",
      platform: "பயன்படுத்தப்பட்ட தளம்",
      telegram: "டெலிகிராம் பயனர்பெயர்",
      upi: "UPI ஐடி",
      phone: "தொலைபேசி எண்",
      company: "நிறுவனம் / ஆலோசகர் பெயர்",
      website: "வலைதள URL",
      description: "என்ன நடந்தது என்பதை விவரிக்கவும்",
      contact: "மின்னஞ்சல் (விருப்பத்தேர்வு)",
      uploadEvidence: "சான்றுகளை பதிவேற்றவும்",
      uploadHelp: "ஸ்கிரீன்ஷாட்கள், PDF, ரசீதுகள். ஒரு கோப்புக்கு அதிகபட்சம் 10MB.",
      submit: "பாதுகாப்பாக சமர்ப்பி",
      successTitle: "புகார் சமர்ப்பிக்கப்பட்டது",
      successDesc: "நன்றி. எங்கள் குழு 72 மணி நேரத்திற்குள் சான்றுகளை மதிப்பாய்வு செய்யும்.",
      disclaimer: "அனைத்து குற்றச்சாட்டுகளுக்கும் சான்று தேவை. தவறான சமர்ப்பிப்புகள் நிராகரிக்கப்படலாம்.",
      placeholders: {
        description: "தேதிகள், தொகைகள், வாக்குறுதிகள் மற்றும் உண்மையில் என்ன நடந்தது என்பதை சேர்க்கவும்.",
      },
      types: [
        "டெலிகிராம் பிரீமியம் குழு",
        "போலி SEBI ஆலோசகர்",
        "ஆராய்ச்சி ஆய்வாளர் / ஆராய்ச்சி ஆலோசனை",
        "கணக்கு நிர்வாக மோசடி",
        "உத்தரவாத லாப திட்டம்",
        "வாட்ஸ்அப் சிக்னல் பம்ப்",
        "போலி லாப ஸ்கிரீன்ஷாட்",
        "ஃபாரெக்ஸ் / கிரிப்டோ மீட்பு மோசடி",
        "போலி படிப்பு மோசடி",
        "மற்றவை",
      ],
    },
    database: {
      title: "மோசடி தரவுத்தளம்",
      subtitle: "தேடக்கூடிய, சான்று-அடிப்படையிலான தரவுத்தளம்.",
      searchPlaceholder: "டெலிகிராம், UPI, தொலைபேசி, நிறுவனத்தைத் தேடுங்கள்…",
      noResults: "எங்கள் தரவுத்தளத்தில் பொருத்தங்கள் இல்லை.",
      victims: "பாதிக்கப்பட்டவர்கள்",
      lossReported: "இழப்பு புகாரளிக்கப்பட்டது",
      statusLabels: {
        high_risk: "அதிக ஆபத்து",
        reported_cybercrime: "சைபர் கிரைமுக்கு புகார்",
        sebi_warning: "SEBI எச்சரிக்கை",
        multiple_complaints: "பல புகார்கள்",
        under_investigation: "விசாரணையில்",
      },
      filterAll: "அனைத்து நிலைகள்",
    },
    risk: {
      title: "மோசடி ஆபத்து சரிபார்ப்பு",
      subtitle: "எங்கள் புகாரளிக்கப்பட்ட மோசடி தரவுத்தளத்தில் சரிபார்க்க உள்ளிடவும்.",
      telegram: "டெலிகிராம் பயனர்பெயர்",
      upi: "UPI ஐடி",
      phone: "தொலைபேசி எண்",
      website: "வலைதள URL",
      check: "ஆபத்து சரிபார்",
      verdictLabels: {
        DANGER: "ஆபத்து — உறுதிப்படுத்தப்பட்ட மோசடி",
        "HIGH RISK": "அதிக ஆபத்து — பல புகார்கள்",
        CAUTION: "கவனிக்க — சாத்தியமான பொருத்தம்",
        "NO MATCH": "எங்கள் தரவுத்தளத்தில் பொருத்தம் இல்லை",
      },
      noMatchHelp: "பொருத்தம் இல்லாதது பாதுகாப்பானது என்று அர்த்தமல்ல. சிவப்பு கொடி கண்டறிதலையும் பயன்படுத்தவும்.",
    },
    redFlag: {
      title: "சிவப்பு கொடி கண்டறிதல்",
      subtitle: "நீங்கள் கவனித்த ஒவ்வொரு நடத்தையையும் டிக் செய்யவும். மோசடி நிகழ்தகவு மதிப்பெண்ணை கணக்கிடுவோம்.",
      checklist: [
        { key: "guaranteed_profits", label: "உத்தரவாத லாபம் அல்லது நிலையான % வருமானத்தை வாக்களிக்கிறார்" },
        { key: "fake_sebi_certificate", label: "SEBI சான்றிதழைக் காட்டுகிறார் ஆனால் sebi.gov.in இல் சரிபார்க்க முடியவில்லை" },
        { key: "asks_remote_access", label: "ரிமோட் அணுகல் அல்லது வர்த்தக உள்நுழைவை கேட்கிறார்" },
        { key: "asks_upi_to_personal", label: "தனிப்பட்ட UPI / கணக்கிற்கு பணம் கேட்கிறார்" },
        { key: "pressure_tactics", label: "அழுத்த தந்திரங்கள் — 'வரம்பு இடங்கள்', 'இன்று மட்டுமே'" },
        { key: "fake_screenshots", label: "தரகர் அறிக்கை இல்லாமல் லாப ஸ்கிரீன்ஷாட்களை காட்டுகிறார்" },
        { key: "no_written_agreement", label: "எழுத்துப்பூர்வ ஒப்பந்தம் இல்லை, GST விலைப்பட்டியல் இல்லை" },
        { key: "unverified_advisor", label: "சரிபார்க்கக்கூடிய சான்றுகள் இல்லாத செல்வாக்கு மிக்கவர் / டெலிகிராம் நிர்வாகி" },
      ],
      score: "மோசடி நிகழ்தகவு",
      flagged: "சிவப்பு கொடிகள் கண்டறியப்பட்டன",
    },
    education: {
      eyebrow: "அறிவுத் தளம்",
      title: "விழிப்புணர்வு மையம்",
      subtitle: "இந்திய சில்லறை வர்த்தகர்களுக்கான மோசடி விழிப்புணர்வு வழிகாட்டிகள்.",
      readMinutes: "நிமி. படிக்க",
      filterAll: "அனைத்தும்",
      emptyCategory: "இந்த வகையில் இன்னும் கட்டுரைகள் இல்லை.",
    },
    blog: {
      eyebrow: "விசாரணை · கதைகள் · வழிகாட்டிகள்",
      title: "வலைப்பதிவு — உண்மை வழக்குகள், சட்ட வழிகாட்டி",
      subtitle: "சரிபார்க்கப்பட்ட பாதிக்கப்பட்டவர்களின் அனுபவங்கள் மற்றும் சட்ட புகார் படிகள்.",
      allStories: "அனைத்து கதைகள்",
      featured: "சிறப்பு",
      readFull: "முழுவதும் படிக்க",
      readArticle: "முழுக் கட்டுரை",
      collapse: "சுருக்கு",
      emptyCategory: "இந்த வகையில் இன்னும் கட்டுரைகள் இல்லை.",
      ctaTitle: "உங்கள் கதையைப் பகிரவா?",
      ctaBody: "அநாமதேய பாதிக்கப்பட்டவர் கதைகள் ஆயிரக்கணக்கானோருக்கு உதவும். உங்கள் வழக்கை அனுப்புங்கள் — சரிபார்த்து வெளியிடுவோம்.",
      ctaButton: "வழக்கை சமர்ப்பிக்க",
    },
    stories: {
      title: "உண்மையான பாதிக்கப்பட்டவர் கதைகள்",
      subtitle: "வங்கி மோசடி, காப்பீடு வழக்குகள், ஷாப்பிங் மோசடி, கடன் செயலிகள் — வர்த்தகத்திற்கு மட்டுப்படுத்தப்படாத நிதி இழப்பு அனுபவங்கள்.",
      lossLabel: "இழப்பு",
      lessonLabel: "நான் கற்றுக்கொண்டது",
    },
    about: {
      title: "CHINTAMAT ஏன் உள்ளது",
      subtitle:
        "இந்தியர்கள் ஒவ்வொரு ஆண்டும் வங்கி மோசடி, சைபர் மோசடி, காப்பீடு தவறான விற்பனை, நுகர்வோர் வழக்குகளில் ஆயிரக்கணக்கான கோடி ரூபாய்களை இழக்கிறார்கள்.",
      mission:
        "CHINTAMAT ஒரு சுயாதீன, சான்று-அடிப்படையிலான தளம் — சரியான புகார் சேனல், சரிபார்க்கக்கூடிய வழக்கு கோப்பு, பொது தரவுத்தளம் மூலம் நிதி இழப்புக்கு எதிராக போராட உதவுகிறது.",
      pillars: [
        { title: "இரகசியத்தன்மை", desc: "பாதிக்கப்பட்டவர் தாமாகவே வெளிப்படுத்த விரும்பினால் தவிர அனைத்து புகார்களும் அநாமதேயம்." },
        { title: "சான்று-ஆதரவு", desc: "சரிபார்க்கக்கூடிய ஆவணங்களால் ஆதரிக்கப்படும் போதே மட்டுமே வழிகாட்டுதல் வெளியிடப்படும்." },
        { title: "அதிகாரப்பூர்வ வழிகாட்டுதல்", desc: "RBI, SEBI, IRDAI, சைபர் கிரைம், நுகர்வோர் போர்டல்கள் — சரிபார்க்கப்படாத மீட்பு முகவர்கள் அல்ல." },
      ],
      notDo: {
        title: "CHINTAMAT செய்யாதவை",
        items: [
          "முதலீட்டு குறிப்புகள், வர்த்தக சிக்னல்கள் அல்லது உத்தரவாத வருமான ஆலோசனை வழங்காது.",
          "சரிபார்க்கப்படாத படிப்புகளை விற்காது; தரகர்/முகவராக செயல்படாது.",
          "யாருடைய வங்கி, டீமேட் அல்லது வர்த்தக கணக்கையும் அணுகவோ நிர்வகிக்கவோ மாட்டாது.",
          "நிதி மீட்புக்கு உத்தரவாதம் அளிக்காது; சட்ட நிறுவனம்/போலீஸாக நடிக்காது.",
          "தேடும் நிறுவனங்களிடமிருந்து கட்டண விளம்பரத்தை ஏற்காது.",
        ],
      },
      disclaimer: "CHINTAMAT ஒரு சட்ட நிறுவனமோ நிதி ஆலோசகரோ அல்ல. அதிகாரப்பூர்வ இந்திய புகார் அமைப்புகளுக்கு வழிநடத்துகிறோம்.",
    },
    contact: {
      title: "தொடர்பு கொள்ளுங்கள்",
      subtitle: "மின்னஞ்சல் விருப்பமான மற்றும் மிகவும் பாதுகாப்பான சேனல். தொலைபேசி ஆதரவு வழங்கப்படவில்லை.",
      name: "உங்கள் பெயர் (அல்லது புனைப்பெயர்)",
      email: "மின்னஞ்சல்",
      subject: "பொருள்",
      message: "செய்தி",
      send: "செய்தியை அனுப்பு",
      typeGeneral: "பொதுவான விசாரணை",
      typeMedia: "ஊடகம் / பத்திரிகை",
      typeAnon: "அநாமதேய தகவல்",
      success: "செய்தி அனுப்பப்பட்டது. 3-5 வேலை நாட்களில் பதிலளிப்போம்.",
    },
    footer: {
      tagline: "கேள்விப்பட்டதை விட சான்றுகள். மௌனத்தை விட பாதுகாப்பு.",
      sections: { platform: "தளம்", legal: "சட்டபூர்வம்", connect: "இணை" },
      links: {
        privacy: "தனியுரிமை கொள்கை",
        terms: "பயன்பாட்டு விதிமுறைகள்",
        disclaimer: "மறுப்பு",
        antiHarassment: "துன்புறுத்தல்-எதிர்ப்பு கொள்கை",
        dataDeletion: "தரவு நீக்க கோரிக்கை",
      },
      disclaimer:
        "CHINTAMAT நிதி ஆலோசனை வழங்காது. நிதி மீட்புக்கு உத்தரவாதம் அளிக்காது. அனைத்து குற்றச்சாட்டுகளுக்கும் சான்று தேவை.",
      rights: "© 2026 CHINTAMAT. பொது-நல மோசடி எதிர்ப்பு பணி.",
    },
    stats: {
      reports: "சரிபார்க்கப்பட்ட புகார்கள்",
      scammers: "கண்காணிக்கப்பட்ட மோசடி",
      cases: "தீர்க்கப்பட்ட வழக்குகள்",
      loss: "இழப்பு புகாரளிக்கப்பட்டது",
    },
  },
};

export const translations = {
  en: deepMergeLocale(_raw.en, appStrings.en),
  hi: deepMergeLocale(deepMergeLocale(_raw.en, _raw.hi), appStrings.hi),
  te: deepMergeLocale(deepMergeLocale(_raw.en, _raw.te), appStrings.te),
  ta: deepMergeLocale(deepMergeLocale(_raw.en, _raw.ta), appStrings.ta),
  bn: deepMergeLocale(deepMergeLocale(_raw.en, rawBn), appStringsBn),
  mr: deepMergeLocale(deepMergeLocale(_raw.en, rawMr), appStringsMr),
  kn: deepMergeLocale(deepMergeLocale(_raw.en, rawKn), appStringsKn),
  ml: deepMergeLocale(deepMergeLocale(_raw.en, rawMl), appStringsMl),
  pa: deepMergeLocale(deepMergeLocale(_raw.en, rawPa), appStringsPa),
};
